import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Login.module.css';
import { HOST } from '../config/api';

const PasswordReset = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [repeatPassword, setRepeatPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const token = searchParams.get('token');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            setMessage('This reset link is incomplete or missing. Please open the password reset link from your email again.');
            setSuccess(false);
            return;
        }

        if (password !== repeatPassword) {
            setMessage('Passwords do not match. Please try again.');
            setSuccess(false);
            return;
        }

        setIsSubmitting(true);

        const payload = {
            token,
            password,
        };

        try {
            const response = await fetch(HOST + '/v1/reset_password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setMessage('Your password has been changed successfully. You can now log in with your new password.');
                setSuccess(true);

                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setMessage('Password reset failed. Please try the link again or request a new reset email.');
                setSuccess(false);
            }
        } catch (error) {
            setMessage('Password reset failed. Please try the link again or request a new reset email.');
            setSuccess(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.authPage}>
            <section className={styles.authShell}>
                <div className={styles.authIntro}>
                    <div className={styles.authImageWrap}>
                        <img src="/black_and_white_image.png" alt="Underwater training" className={styles.authImage} />
                        <div className={styles.authImageOverlay}>
                            <span>Lead the Way</span>
                            <strong>Reset your password without losing momentum.</strong>
                            <p>We keep the reset step simple so you can get back into your training flow quickly.</p>
                        </div>
                    </div>

                    <div className={styles.authCopy}>
                        <span className={styles.authEyebrow}>Password reset</span>
                        <h1>Create a new password and return to the app.</h1>
                        <p>Choose a new password below. Once it’s saved, you’ll be able to log in right away.</p>
                    </div>
                </div>

                <div className={styles.authPanel}>
                    {success ? (
                        <div className={styles.successPanel}>
                            <span className={styles.panelKicker}>Password updated</span>
                            <h2>All set</h2>
                            <p className={styles.registrationMessageSuccess}>{message}</p>
                            <p className={styles.registrationMessageSuccess}>Redirecting to login shortly. You can also continue immediately.</p>
                            <Link className={styles.primaryButton} to="/login">
                                Go to login
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className={styles.panelHeader}>
                                <span className={styles.panelKicker}>Reset password</span>
                                <h2>Choose your new password</h2>
                                <p>Enter the same password twice so we can update your account safely.</p>
                            </div>

                            <form className={styles.authForm} onSubmit={handleSubmit}>
                                <div className={styles.formGroup}>
                                    <label className={styles.fieldLabel} htmlFor="password">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        className={styles.inputField}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Create a new password"
                                        required
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.fieldLabel} htmlFor="repeatPassword">
                                        Repeat password
                                    </label>
                                    <input
                                        type="password"
                                        id="repeatPassword"
                                        className={styles.inputField}
                                        value={repeatPassword}
                                        onChange={(e) => setRepeatPassword(e.target.value)}
                                        placeholder="Repeat your new password"
                                        required
                                    />
                                </div>

                                {message ? (
                                    <p className={success ? styles.registrationMessageSuccess : styles.registrationMessageFailure}>
                                        {message}
                                    </p>
                                ) : null}

                                <div className={styles.actionStack}>
                                    <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>
                                        {isSubmitting ? 'Saving new password...' : 'Save new password'}
                                    </button>
                                    <Link className={styles.secondaryButton} to="/login">
                                        Back to login
                                    </Link>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default PasswordReset;
